import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { getMediaSaveErrorCopy } from "../services/app/validation";
import {
  trackPhotoCompressFailed,
  trackPhotoCompressStarted,
  trackPhotoCompressSuccess,
  trackPhotoPickerOpened,
  trackPhotoSaveFailed,
  trackPhotoSaveSuccess,
  trackPhotoSelected
} from "../services/analytics/analyticsEvents";
import { createIssueFromPhoto } from "../services/db/reportRepo";
import type { CompressedPhotoInput } from "../services/db/reportRepo";
import { compressImage } from "../services/media/imageCompression";
import { collectMediaSaveFailureDiagnostics } from "../services/media/mediaDiagnostics";
import { mimeCategory } from "../services/analytics/analyticsRedaction";
import { isNativeApp } from "../services/platform/runtime";
import { CameraAccessDeniedError, captureNativePhoto } from "../services/platform/nativeCapture";
import { openAppSettings } from "../services/platform/appSettings";

export interface CaptureFlowHandle {
  open: () => void;
}

interface CaptureFlowProps {
  reportId: string;
  roomId: string;
  onCreated: (issueId: string) => void;
  onError: (message: string) => void;
}

export const CaptureFlow = forwardRef<CaptureFlowHandle, CaptureFlowProps>(function CaptureFlow(
  { reportId, roomId, onCreated, onError },
  ref
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [cameraAccessDenied, setCameraAccessDenied] = useState(false);

  useImperativeHandle(ref, () => ({
    open() {
      if (saving) return;
      trackPhotoPickerOpened();
      if (isNativeApp()) { void nativeCapture(); return; }
      inputRef.current?.click();
    }
  }));

  async function nativeCapture(): Promise<void> {
    if (saving) return;
    setSaving(true);
    setCameraAccessDenied(false);
    try {
      const issue = await captureNativePhoto(reportId, roomId);
      if (issue) onCreated(issue.id);
    } catch (error) {
      if (error instanceof CameraAccessDeniedError) {
        setCameraAccessDenied(true);
        onError("");
      } else {
        onError(error instanceof Error ? error.message : "Could not save the photo. Please try again.");
      }
    } finally { setSaving(false); }
  }

  async function handleFile(file: File | undefined) {
    if (!file) {
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setSaving(true);
    let compressed: CompressedPhotoInput | undefined;
    try {
      trackPhotoSelected(file);
      trackPhotoCompressStarted(file);
      const compressionStartedAt = performance.now();
      compressed = await compressImage(file);
      trackPhotoCompressSuccess({
        originalBytes: file.size,
        compressedBytes: compressed.fullBlob.size,
        thumbBytes: compressed.thumbBlob.size,
        width: compressed.width,
        height: compressed.height,
        mimeType: compressed.fullBlob.type,
        elapsedMs: Math.round(performance.now() - compressionStartedAt)
      });
      const issue = await createIssueFromPhoto(reportId, roomId, compressed);
      trackPhotoSaveSuccess({
        compressedBytes: compressed.fullBlob.size,
        thumbBytes: compressed.thumbBlob.size,
        width: compressed.width,
        height: compressed.height
      });
      navigator.vibrate?.(10);
      onCreated(issue.id);
    } catch (error) {
      if (compressed) {
        const diagnostics = await collectMediaSaveFailureDiagnostics({
          compressedBytes: compressed.fullBlob.size,
          thumbBytes: compressed.thumbBlob.size,
          width: compressed.width,
          height: compressed.height,
          mimeCategory: mimeCategory(compressed.fullBlob.type)
        });
        trackPhotoSaveFailed(error, diagnostics);
      } else {
        trackPhotoCompressFailed(error, file.size, file.type);
      }
      onError(compressed ? getMediaSaveErrorCopy(error, "photo") : "Could not process this photo on this device.");
    } finally {
      setSaving(false);
      setPreviewUrl(undefined);
      URL.revokeObjectURL(objectUrl);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  }

  return (
    <>
      {cameraAccessDenied && <div className="notice" role="alert">
        <p>Camera access is off. Allow it in Settings, then return and tap Take photo.</p>
        <button className="button button--secondary" type="button" onClick={() => {
          void openAppSettings().catch(() => onError("Could not open Settings. Please try again."));
        }}>Open Settings</button>
      </div>}
      {isNativeApp() && saving && !previewUrl && <div className="notice" role="status">Saving photo on this device...</div>}
      {!isNativeApp() && <input
        ref={inputRef}
        className="hidden-input"
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="Take condition note photo"
        onChange={(event) => {
          void handleFile(event.target.files?.[0]);
        }}
      />}
      {previewUrl && (
        <div className="dialog-backdrop" role="presentation">
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="capture-title">
            <img className="capture-preview" src={previewUrl} alt="Selected condition note" />
            <div className="dialog__body">
              <div className="topbar">
                <div>
                  <h2 id="capture-title">Saving photo</h2>
                  <p>{saving ? "Compressing on this device..." : "Saved."}</p>
                </div>
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Close"
                  disabled={saving}
                  onClick={() => setPreviewUrl(undefined)}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              <div className="button-row">
                <span className="button button--primary" aria-live="polite">
                  <Camera size={18} aria-hidden="true" />
                  {saving ? "Saving..." : "Saved. Keep going."}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
